/**
 * Single client for the **Credit-Score-Service (ClickHouse)** API.
 *
 * This is the one file that knows the wire format of the new backend. Everything else
 * (stores, views) calls the named helpers below and never builds a URL or a request body
 * by hand — that is the whole point of this module and the reason this frontend exists
 * as a separate copy of `pawnshop-admin-new`.
 *
 * ── What changed vs the old credit-customer engine (`/api/**` + `/insight/**` on :9090)
 *
 *   old:  GET  /api/score/results?groupUid=..&shopUid=..&page=0&size=100&search=&tier=
 *   new:  POST /score-svc/score/results/list
 *         { "page": { "index": 0, "size": 100, "sort": "" },
 *           "filter": { "groupUid": "..", "shopUid": "..", "search": "", "tier": "" } }
 *
 *   old:  GET  /api/score/results/{cusUid}?groupUid=..&shopUid=..
 *   new:  POST /score-svc/score/results   { groupUid, shopUid, cusUid }
 *
 *   Everything else keeps its path but moves GET+query-string → POST+JSON body, with
 *   `groupUid`/`shopUid` as flat body fields. Multipart uploads are unchanged in shape
 *   (file + groupUid + shopUid form fields), only the path prefix moved.
 *
 * ── The real service wraps everything (found 2026-08-02 against dev-api.nvtx.tech)
 *
 * The Postman collection documents requests only, so this was invisible until the service was called
 * for real. Every response comes back in the standard NVTX platform envelope:
 *
 *     { "rtnCode": 0, "rtnDesc": "success", "timestamp": "…", "time": "0", "data": <the DTO> }
 *
 * `rtnCode` 0 = success, non-zero = failure (`rtnDesc` carries the reason). `unwrap()` below peels it
 * off, so the stores keep seeing the same DTOs they always did. It tolerates a bare DTO too, because
 * `dev/score-svc-bridge.js` speaks the old engine's un-enveloped shape.
 *
 * The paged endpoints are **not consistent with each other**, which no amount of reading the
 * collection would have told you:
 *
 *     /score/results/list  → data = { list: [...], ext: { total, totalPages } }
 *     /score/overdue       → data = { page: { index, size, total, totalPages }, data: [...] }
 *     /score/warning       → same as /score/overdue
 *
 * `toPagedResponse()` normalises both into the `{ page, data }` shape the stores expect. `ext` has no
 * `index`/`size`, so those are carried over from the request.
 *
 * ── Response DTOs themselves are unchanged — `PagedScoreResponse { page:{index,size,total,totalPages}, data:[] }`,
 * `UploadScoreResponse`, `LeaderboardEntry`, `InterestRatioEntry`, `ExpiringTicketForecast`,
 * and every insight DTO come back byte-for-byte as before, so no view code had to change
 * how it *reads* data.
 *
 * ── Endpoints marked `MISSING FROM THE COLLECTION EXPORT`
 *
 * Four endpoints the UI needs are absent from
 * `collection/Credit-Score-Service (clickhouse).postman_collection.json` — that file was
 * exported from an older collection bundled with the project, not from the current service.
 * **The service does implement them**; only the export is stale. They follow the same
 * conventions as everything else here.
 *
 * ── Row caps: `n` almost everywhere, `limit` on exactly one endpoint
 *
 * Measured against dev-api on 2026-08-02, because a wrong name here is invisible — the service
 * answers `rtnCode: 0 success` with exactly **one** row rather than erroring, so the page just looks
 * like it has thin data:
 *
 *     leaderboard/forfeit-forecast | open-principal | open-tickets   → n
 *     report/interest-ratio/top                                      → n
 *     forecast/expiring                                              → limit  (plus days)
 *
 * The default is **1**, not the old engine's 100/500, so the cap can never be omitted either.
 */

/* eslint-disable no-unused-vars */

import http from "../config/apiConfig.js";

import { authHeaders, clearAuth } from './creditAuth.js'

/** Gateway prefix for the new service. Proxied by vue.config.js in dev. */
export const SCORE_SVC = '/score-svc'

/** Thrown for any non-2xx response so callers can inspect `status`. */
export class ScoreSvcError extends Error {
  constructor(message, status, path) {
    super(message)
    this.name = 'ScoreSvcError'
    this.status = status
    this.path = path
  }
}

/**
 * Scope of the currently selected shop, as strings.
 *
 * Admin app: scope comes from `shop_seleted` (the shop chosen in the header switcher),
 * not from the JWT. groupUid/shopUid are Long values well past Number.MAX_SAFE_INTEGER,
 * so they must never round-trip through a JS number — always Strings.
 *
 * Returns `{}` when no real shop is selected (uid missing or the "all shops" sentinel '0'),
 * matching the old `scopeQueryString()` behaviour of simply omitting the params.
 */
export function currentScope() {
  let shopData = null
  try {
    const raw = localStorage.getItem('shop_seleted')
    shopData = raw ? JSON.parse(raw) : null
  } catch (_e) {
    return {}
  }
  if (!shopData?.uid || shopData.uid === '0') return {}
  const scope = { shopUid: String(shopData.uid) }
  if (shopData.groupUid) scope.groupUid = String(shopData.groupUid)
  return scope
}

/** True when a real shop is selected — upload paths require this. */
export function hasScope() {
  return Boolean(currentScope().shopUid)
}

/**
 * POST a JSON body to `/score-svc{path}` with the current scope merged in.
 * `body` fields win over the scope so an explicit override is always possible.
 */

// 1. ฟังก์ชัน postJson
export async function postJson(path, body = {}) {
  const payload = { ...currentScope(), ...body };

  // นำ '/score-svc' มาต่อหน้า path เพื่อไม่ให้ URL ผิดพลาด
  const fullPath = `${SCORE_SVC}${path}`;
  const response = await http.post(fullPath, payload);

  return unwrap(response.data, path);
}

/**
 * POST a paged request using the collection's `{ page, filter }` envelope.
 * `filter` is merged on top of the scope, so groupUid/shopUid always travel inside `filter`
 * (not at the top level) — that is where the collection puts them for list endpoints.
 */

// 2. ฟังก์ชัน postPaged
export async function postPaged(path, { index = 0, size = 100, sort = '' } = {}, filter = {}) {
  const payload = {
    page: { index, size, sort },
    filter: { search: '', tier: '', ...currentScope(), ...filter },
  };

  // นำ '/score-svc' มาต่อหน้า path เช่นกัน
  const fullPath = `${SCORE_SVC}${path}`;
  const responsePayload = await http.post(fullPath, payload);

  const unwrappedData = unwrap(responsePayload.data, path);
  return toPagedResponse(unwrappedData, index, size);
}

/**
 * POST a multipart upload. `groupUid`/`shopUid` ride along as text form fields exactly
 * as the collection's "Upload Excel" request does. Content-Type is deliberately NOT set —
 * the browser must add its own multipart boundary.
 */

// 3. ฟังก์ชัน postFile
export async function postFile(path, file, scope = currentScope()) {
  const form = new FormData()
  form.append('file', file)
  form.append('groupUid', scope.groupUid || '')
  form.append('shopUid', scope.shopUid || '')

  // นำ '/score-svc' มาต่อหน้า path ให้ครบถ้วน
  const fullPath = `${SCORE_SVC}${path}`;
  const response = await http.post(fullPath, form);

  return unwrap(response.data, path);
}

async function request(path, options) {
  const url = `${SCORE_SVC}${path}`
  const res = await fetch(url, options)
  if (res.status === 401) {
    clearAuth()
    throw new ScoreSvcError('Session expired', 401, path)
  }
  if (!res.ok) {
    throw new ScoreSvcError(await errorMessage(res, path), res.status, path)
  }
  return unwrap(await parseBody(res), path)
}

/**
 * Peel the platform envelope off a response.
 *
 * A failed call still arrives as HTTP 200 with `rtnCode: 1`, so this is the only place that failure is
 * detectable — without it the stores would happily render `undefined` everywhere. Bodies without an
 * `rtnCode` pass through untouched, which is what keeps `dev/score-svc-bridge.js` working.
 */
function unwrap(body, path) {
  if (!body || typeof body !== 'object' || !('rtnCode' in body)) return body
  if (Number(body.rtnCode) !== 0) {
    throw new ScoreSvcError(body.rtnDesc || `${path} -> rtnCode ${body.rtnCode}`, 200, path)
  }
  return body.data
}

/**
 * Normalise the two different paged shapes into `{ page, data }`.
 * `index`/`size` come from the request because the `{ list, ext }` form does not echo them back.
 */
function toPagedResponse(payload, index, size) {
  if (payload && Array.isArray(payload.list)) {
    const ext = payload.ext || {}
    return {
      page: { index, size, total: ext.total ?? payload.list.length, totalPages: ext.totalPages ?? 0 },
      data: payload.list,
    }
  }
  if (payload && Array.isArray(payload.data)) return payload
  return { page: { index, size, total: 0, totalPages: 0 }, data: [] }
}

async function parseBody(res) {
  const text = await res.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch (_e) {
    return text
  }
}

async function errorMessage(res, path) {
  try {
    const text = await res.text()
    if (!text) return `${path} -> ${res.status}`
    try {
      const data = JSON.parse(text)
      return data.message || data.error || `${path} -> ${res.status}`
    } catch (_e) {
      return text.slice(0, 300)
    }
  } catch (_e) {
    return `${path} -> ${res.status}`
  }
}

/**
 * `growth/quality` returns `periodStarts`/`periodEnds` as plain dates (`2026-01-01`), but the
 * collection's asset-breakdown example sends `2026-01-01T00:00:00`. Normalising here means the
 * drill-down keeps working whichever of the two the backend actually parses.
 */
export function toDateTime(value) {
  if (!value) return value
  const s = String(value)
  return s.includes('T') ? s : `${s}T00:00:00`
}

// ─────────────────────────────────────────────────────────────────────────────
// CreditScore
// ─────────────────────────────────────────────────────────────────────────────

export const scoreApi = {
  /** POST /score-svc/score/status → { hasData, customerCount, lastScoreDate } */
  status: () => postJson('/score/status'),

  /** POST /score-svc/score/summary → ScoreSummaryResponse */
  summary: () => postJson('/score/summary'),

  /** POST /score-svc/score/results/list → PagedScoreResponse */
  results: (page, filter) => postPaged('/score/results/list', page, filter),

  /** POST /score-svc/score/results → UploadScoreResponse (single customer) */
  result: (cusUid) => postJson('/score/results', { cusUid: String(cusUid) }),

  /** POST /score-svc/score/overdue → PagedScoreResponse */
  overdue: (page, filter) => postPaged('/score/overdue', page, filter),

  /** POST /score-svc/score/warning → PagedScoreResponse */
  warning: (page, filter) => postPaged('/score/warning', page, filter),

  /** POST /score-svc/score/upload (multipart) → UploadScoreResponse[] */
  upload: (file, scope) => postFile('/score/upload', file, scope),

  /** POST /score-svc/score/upload-delta (multipart) → DeltaUploadResponse */
  uploadDelta: (file, scope) => postFile('/score/upload-delta', file, scope),

  /**
   * POST /score-svc/score/upload-delta/preview (multipart) → DeltaPreviewResponse
   *
   * MISSING FROM THE COLLECTION EXPORT (the service implements it). The dry-run half of
   * the two-step delta upload — identical multipart shape to `upload` / `upload-delta`.
   */
  uploadDeltaPreview: (file, scope) => postFile('/score/upload-delta/preview', file, scope),
}

// ─────────────────────────────────────────────────────────────────────────────
// Leaderboard
// ─────────────────────────────────────────────────────────────────────────────

export const leaderboardApi = {
  /** POST /score-svc/leaderboard/years → number[] (descending) */
  years: () => postJson('/leaderboard/years'),

  /** POST /score-svc/leaderboard/revenue → LeaderboardEntry[] */
  revenue: (year) => postJson('/leaderboard/revenue', year ? { year } : {}),

  /** POST /score-svc/leaderboard/forfeiture-count → LeaderboardEntry[] */
  forfeitureCount: (year) => postJson('/leaderboard/forfeiture-count', year ? { year } : {}),

  /** POST /score-svc/leaderboard/forfeiture-ratio → LeaderboardEntry[] */
  forfeitureRatio: (year) => postJson('/leaderboard/forfeiture-ratio', year ? { year } : {}),

  /**
   * The three "open portfolio" boards below return `UploadScoreResponse[]` (not LeaderboardEntry)
   * and take a row cap instead of a year.
   *
   * ⚠️ The row-cap parameter is **`n`**, and getting it wrong fails silently: the service answers
   * `rtnCode: 0 success` with exactly **one** row, so the page renders a single entry and nothing
   * anywhere reports a problem. Measured against dev-api on 2026-08-02 — `{n:100}` → 100 rows,
   * `{limit:100}` → 1 row, omitted → 1 row. Note the default is 1, not the old engine's 100, so
   * the cap can never be left out.
   *
   * MISSING FROM THE COLLECTION EXPORT (the service implements them) — they back the
   * เสี่ยงหลุด / เงินต้นคงค้าง / ตั๋วเปิด tabs of the Top-100 page.
   */
  forfeitForecast: (n = 100) => postJson('/leaderboard/forfeit-forecast', { n }),
  openPrincipal: (n = 100) => postJson('/leaderboard/open-principal', { n }),
  openTickets: (n = 100) => postJson('/leaderboard/open-tickets', { n }),
}

// ─────────────────────────────────────────────────────────────────────────────
// Forecast & Reports
// ─────────────────────────────────────────────────────────────────────────────

export const forecastApi = {
  /**
   * POST /score-svc/forecast/expiring → ExpiringTicketForecast[]
   * The one endpoint whose row cap really is `limit` — `{n}` here returns 1 row. Verified 2026-08-02.
   */
  expiring: (days = 15, limit = 500) => postJson('/forecast/expiring', { days, limit }),
}

export const reportApi = {
  /** POST /score-svc/report/interest-ratio/top → InterestRatioEntry[] */
  interestRatioTop: (n = 100) => postJson('/report/interest-ratio/top', { n }),

  /** POST /score-svc/report/interest-ratio/over-principal → InterestRatioEntry[] */
  interestRatioOverPrincipal: () => postJson('/report/interest-ratio/over-principal'),
}

// ─────────────────────────────────────────────────────────────────────────────
// CustomerInsight
// ─────────────────────────────────────────────────────────────────────────────

export const insightApi = {
  /** POST /score-svc/insight/overview → OverviewResponse */
  overview: () => postJson('/insight/overview'),

  /** POST /score-svc/insight/behavior → BehaviorResponse (year omitted = all time) */
  behavior: (year) => postJson('/insight/behavior', year ? { year } : {}),

  /** POST /score-svc/insight/pareto → ParetoResponse */
  pareto: (year) => postJson('/insight/pareto', year ? { year } : {}),

  /** POST /score-svc/insight/rfm → RfmResponse */
  rfm: () => postJson('/insight/rfm'),

  /** POST /score-svc/insight/growth/base → GrowthBaseResponse ('month'|'quarter'|'year'|'day')
   *  `month` ("yyyy-MM") only matters for period='day' — selects which calendar month to break
   *  into daily buckets; omitted for the other period types. */
  growthBase: (period = 'month', month) => postJson('/insight/growth/base', month ? { period, month } : { period }),

  /** POST /score-svc/insight/growth/quality → GrowthQualityResponse (same `month` contract as growthBase) */
  growthQuality: (period = 'month', month) => postJson('/insight/growth/quality', month ? { period, month } : { period }),

  /** POST /score-svc/insight/revenue/by-band → RevenueByBandResponse */
  revenueByBand: (period = 'month') => postJson('/insight/revenue/by-band', { period }),

  /** POST /score-svc/insight/portfolio/asset-breakdown → AssetBreakdownResponse */
  assetBreakdown: (txType, startDate, endDate, customerType) =>
    postJson('/insight/portfolio/asset-breakdown', {
      txType,
      startDate: toDateTime(startDate),
      endDate: toDateTime(endDate),
      customerType: customerType || '',
    }),
}
